import type {
  AgencySpendShareData,
  NewOldCustomerSpendShareData,
  OperationDashboardCardsData,
  QueryAgencySpendShareParams,
  QueryCustomerRankParams,
  QueryNewOldCustomerSpendShareParams,
  QueryOperationDashboardCardsParams,
} from './types/ad-daily-report'

import type { PagingRequest } from '#/api/type'

import { defineApi } from '#/api/define-api'
import { requestClient } from '#/api/request'

export const adDailyReportApi = defineApi('operation', ({ prefix }) => {
  /**
   * 查询消耗客户日排行（广告日报 Top 卡片）
   * POST /gobestads/operation/customerFundChangeDaily/queryCustomerSpendRankDaily
   */
  function queryAdDailyCustomerRank(
    params: PagingRequest<QueryCustomerRankParams>,
  ) {
    return requestClient
      .post<{
        list?: Array<{
          companyName?: string
          customerId?: null | number | string
          spend?: number
        }>
      }>(prefix('customerFundChangeDaily/queryCustomerSpendRankDaily'), {
        condition: {
          date: params.statDate,
        },
        pageIndex: params.pageIndex,
        pageSize: params.pageSize,
      })
      .then((res) => {
        const list = res.list || []
        return list.map((item) => ({
          customerId: String(item.customerId ?? '-'),
          customerName: item.companyName || '',
          spend: item.spend || 0,
        }))
      })
  }

  /**
   * 查询广告日报代理消耗占比
   * POST /gobestads/operation/customerFundChangeDaily/queryAgencySpendShare
   */
  function queryAgencySpendShare(params: QueryAgencySpendShareParams) {
    return requestClient
      .post<AgencySpendShareData | null>(
        prefix('customerFundChangeDaily/queryAgencySpendShare'),
        {
          statDate: params.statDate,
        },
      )
      .then((res) => ({
        currency: res?.currency,
        items:
          res?.items.map((item) => ({
            agency: item.agency || '未设置',
            percent: item.percent,
            spend: item.spend,
          })) ?? [],
        statDate: res?.statDate,
        totalSpend: res?.totalSpend,
      }))
  }

  /**
   * 查询新老客户当日消耗占比
   * POST /gobestads/operation/customerFundChangeDaily/queryNewOldCustomerSpendShare
   */
  function queryNewOldCustomerSpendShare(
    params: QueryNewOldCustomerSpendShareParams,
  ) {
    return requestClient
      .post<NewOldCustomerSpendShareData>(
        prefix('customerFundChangeDaily/queryNewOldCustomerSpendShare'),
        {
          statDate: params.statDate,
        },
      )
      .then((res) => ({
        newCustomerSpend: res.newCustomerSpend || 0,
        oldCustomerSpend: res.oldCustomerSpend || 0,
      }))
  }

  /**
   * 查询广告日报右侧卡片
   * POST /gobestads/operation/customerFundChangeDaily/queryOperationDashboardCards
   */
  function queryOperationDashboardCards(
    params: QueryOperationDashboardCardsParams,
  ) {
    return requestClient
      .post<OperationDashboardCardsData>(
        prefix('customerFundChangeDaily/queryOperationDashboardCards'),
        {
          statDate: params.statDate,
          month: params.month,
        },
      )
      .then((res) => ({
        activeCustomerCountLast30Days: res.activeCustomerCountLast30Days || 0,
        dayActiveCustomerCount: res.dayActiveCustomerCount || 0,
        dayEstimatedProfit: res.dayEstimatedProfit || 0,
        dayRechargeTotal: res.dayRechargeTotal || 0,
        daySpendTotal: res.daySpendTotal || 0,
        month: res.month || params.month,
        monthRechargeTotal: res.monthRechargeTotal || 0,
      }))
  }

  return {
    queryAdDailyCustomerRank,
    queryAgencySpendShare,
    queryNewOldCustomerSpendShare,
    queryOperationDashboardCards,
  }
})
